/** Settings: the switches the app already had and no player could reach.
 *
 * Everything on this screen was already implemented somewhere and wired to
 * nothing a player can press. `setAnimationsEnabled` had exactly one caller and
 * it was `debug.ts`; the adventure screen's `uiScale` prop existed for the
 * screenshot harness alone; the autosave this build now writes had to be
 * refusable by whoever does not want a megabyte written into local storage
 * every turn. A capability the code has and the UI hides is the same defect as
 * a screen nothing mounts, one layer down.
 *
 * It is a leaf like every other screen here: it holds no preference, it reads
 * none, and it writes none. The host owns the values and the storage.
 */

import { type ReactNode } from "react";
import type { AudioVolumes } from "../audio/engine.js";
import { DEFAULT_VOLUMES, VOLUME_STEPS } from "../audio/engine.js";
import { usePhoneSheetFit } from "../theme/phone.js";
import { usePolishStyles } from "./polish.js";
import { ScreenStyles } from "./primitives.js";
import { FORM_ART_CSS, PaintedPlate } from "./formArt.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/** `null` panel scale means "let the kit's own media steps choose". */
export interface SettingsValues {
  readonly animations: boolean;
  readonly autosave: boolean;
  readonly panelScale: number | null;
  /**
   * Held here rather than inside the audio engine for the same reason the
   * autosave flag is held here: the host owns storage, the engine owns the
   * graph, and a preference the engine remembered would be a second place a
   * value lives.
   */
  readonly volumes: AudioVolumes;
}

export interface SettingsProps {
  readonly values: SettingsValues;
  readonly onChange: (next: SettingsValues) => void;
  readonly onClose: () => void;
  /** Shown as a reference card. A binding nobody can discover is a bug, and
   *  the adventure screen's own help is only reachable from the map. */
  readonly hotkeys?: readonly { readonly combo: string; readonly description: string }[];
  /**
   * An extra card, rendered after the built-in ones.
   *
   * A slot rather than a prop per feature: the offline download this was added
   * for lives in the web app (it talks to a service worker, which is a host
   * concern and not a kit one), and `@game/ui` must not import from `apps/`.
   * The kit owns the shape of a settings screen; the host owns what else
   * belongs on it.
   */
  readonly extra?: ReactNode;
}

export const PANEL_SCALES: readonly { readonly id: string; readonly value: number | null }[] =
  Object.freeze([
    { id: "Automatic", value: null },
    { id: "100%", value: 1 },
    { id: "125%", value: 1.25 },
    { id: "150%", value: 1.5 },
  ]);

export const DEFAULT_SETTINGS: SettingsValues = Object.freeze({
  animations: true, autosave: true, panelScale: null, volumes: DEFAULT_VOLUMES,
});

function Toggle(props: {
  readonly label: string;
  readonly note: string;
  readonly on: boolean;
  readonly onSet: (on: boolean) => void;
}): JSX.Element {
  return (
    <div className="hs-field" style={{ marginBottom: "12px" }}>
      <div className="hs-field__label">{props.label}</div>
      <div className="hs-choices" role="group" aria-label={props.label}>
        <button
          type="button"
          className={`hs-choice ${props.on ? "hs-choice--on" : ""}`}
          aria-pressed={props.on}
          onClick={() => props.onSet(true)}
        >
          On
        </button>
        <button
          type="button"
          className={`hs-choice ${props.on ? "" : "hs-choice--on"}`}
          aria-pressed={!props.on}
          onClick={() => props.onSet(false)}
        >
          Off
        </button>
      </div>
      <div className="hs-tiny hs-muted">{props.note}</div>
    </div>
  );
}

/** The Off/Quiet/Normal/Loud row. Buttons rather than a slider because the kit
 *  has no slider and four steps is every choice anybody makes with one. */
function VolumeRow(props: {
  readonly label: string;
  readonly note: string;
  readonly value: number;
  readonly onSet: (v: number) => void;
}): JSX.Element {
  return (
    <div className="hs-field" style={{ marginBottom: "12px" }}>
      <div className="hs-field__label">{props.label}</div>
      <div className="hs-choices" role="group" aria-label={props.label}>
        {VOLUME_STEPS.map((s) => {
          const on = Math.abs(s.value - props.value) < 0.01;
          return (
            <button
              key={s.id}
              type="button"
              className={`hs-choice ${on ? "hs-choice--on" : ""}`}
              aria-pressed={on}
              onClick={() => { props.onSet(s.value); }}
            >
              {s.id}
            </button>
          );
        })}
      </div>
      <div className="hs-tiny hs-muted">{props.note}</div>
    </div>
  );
}

export function Settings(props: SettingsProps): JSX.Element {
  usePolishStyles();
  const v = props.values;
  const set = (patch: Partial<SettingsValues>): void => { props.onChange({ ...v, ...patch }); };
  const hotkeys = props.hotkeys ?? [];

  const fit = usePhoneSheetFit();
  return (
    <div className={`hs-screen hs-screen--sheet hm-set ${fit.screenClass}`.trim()}
      ref={fit.screenRef} style={fit.style} role="region" aria-label="Settings">
      <ScreenStyles />
      <SettingsStyles />

      {/* The veil is OPAQUE now, and the reasoning that made it a filter is
          reversed on the evidence.
          It was `backdrop-filter: brightness(0.52) saturate(0.74)`, and the
          measurement behind that is still right as far as it goes: black at
          alpha a maps L to L(1-a) and so lifts the ground as fast as it lowers
          a gilt letterform, while brightness() multiplies and takes the
          highlight down. What it could not do is take it away. An art director
          looking at the shipped result read "BANNERS OF THE REALM" across
          the top of this screen at metre height and called it worse than
          emptiness, which it is: a legible word that means nothing here is a
          harder thing to ignore than a dark field. There is no exposure that
          fixes that, only an opaque one. The picture this screen was short of
          is now an object on it - the plate the cards stand on - rather than a
          ghost of the room the door was opened from.
          The grain stays on the veil for the same reason it always was: a wash
          is the one surface that measures as empty however dark it is. */}
      <span className="hm-set__veil" aria-hidden="true" />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title hm-cap">Settings</span>
        <span className="hs-screen__sub hs-muted">Kept in this browser</span>
        <span className="hs-spacer" />
        <button
          type="button"
          className="hs-plate hs-plate--sm"
          title="Put every setting back the way it shipped"
          onClick={() => { props.onChange(DEFAULT_SETTINGS); }}
        >
          Defaults
        </button>
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-screen__body" style={{ justifyContent: "center", padding: "14px" }}>
        {/* The cards stand ON a painting, which is the whole of what separated
            this screen from the eighteen that already read as one game. The classic's
            own options panel is a painted plate with switches cut into it, not
            a dialog floating on a gradient. */}
        <PaintedPlate className="hm-set__stage" atlasKey="town.frostmark.background" focusY={0.44}>
        <div className="hs-scroll hm-set__cols">
          <div className="hs-card hs-wood">
            <div className="hs-card__title">Presentation</div>

            <Toggle
              label="Animations"
              note={"Off makes every move and every blow resolve at once. The turn is the "
                + "same either way; only the waiting changes."}
              on={v.animations}
              onSet={(on) => { set({ animations: on }); }}
            />

            <div className="hs-field">
              <div className="hs-field__label">Panel Size</div>
              <div className="hs-choices" role="group" aria-label="Panel size">
                {PANEL_SCALES.map((s) => {
                  const on = s.value === v.panelScale;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`hs-choice ${on ? "hs-choice--on" : ""}`}
                      aria-pressed={on}
                      onClick={() => { set({ panelScale: s.value }); }}
                    >
                      {s.id}
                    </button>
                  );
                })}
              </div>
              <div className="hs-tiny hs-muted">
                How large the command column and its plates are drawn. Automatic picks
                a size from the window; a fixed one gives the map back the difference.
              </div>
            </div>
          </div>

          <div className="hs-card hs-wood">
            <div className="hs-card__title">Sound</div>
            <VolumeRow
              label="Master"
              note={"Everything at once. Off here silences the game whatever the two "
                + "rows below say."}
              value={v.volumes.master}
              onSet={(master) => { set({ volumes: { ...v.volumes, master } }); }}
            />
            <VolumeRow
              label="Effects"
              note={"Blows, spells, coins, a town changing hands. The shell's own clicks "
                + "ride a little under these."}
              value={v.volumes.sfx}
              onSet={(sfx) => { set({ volumes: { ...v.volumes, sfx } }); }}
            />
            <VolumeRow
              label="Music"
              note={"Four short pieces, one per screen, played rather than recorded - there "
                + "are no audio files in this build. Nothing sounds until you first click "
                + "the page, which is a rule of the browser and not a setting."}
              value={v.volumes.music}
              onSet={(music) => { set({ volumes: { ...v.volumes, music } }); }}
            />
          </div>

          <div className="hs-card hs-wood">
            <div className="hs-card__title">Saving</div>
            <Toggle
              label="Autosave"
              note={"Writes the autosave slot at the top of each of your turns, which is "
                + "what Load Game reaches for. A whole map is about a megabyte and the "
                + "browser allows the page five, so turning this off is a real choice."}
              on={v.autosave}
              onSet={(on) => { set({ autosave: on }); }}
            />
          </div>

          {hotkeys.length > 0 ? (
            <div className="hs-card hs-wood hm-set__keys">
              <div className="hs-card__title">Keys</div>
              <div className="hs-list hm-set__keylist">
                {hotkeys.map((h) => (
                  <div key={h.combo} className="hs-listrow" style={{ cursor: "default" }}>
                    <span className="hs-num" style={{ minWidth: "72px", color: "#f3ddab" }}>{h.combo}</span>
                    <span className="hs-listrow__meta">{h.description}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {props.extra ?? null}
        </div>
        </PaintedPlate>
      </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const SETTINGS_STYLE_ID = "hm-settings-art";

/* Mounted after the kit's sheets, so equal-specificity rules here win on
   document order. No backticks below - this is a template literal. */
export const SETTINGS_CSS = `
/* The screen was one 700px column down the middle of whatever the window was.
   At 1920 that is 1,220px of stone doing nothing, the key list ran off the
   bottom edge mid-row, and every On/Off pair was 340px wide because a choice
   stretched to fill its row. Two columns, a masonry-ish flow, and choices the
   width of their own labels. */
/* The keyword "safe" matters: the content of this screen is taller than 768 with
   a long key list, and a plain centred flex item that overflows overflows in BOTH
   directions, so the top of the column goes above the frame where nothing can
   scroll it back. A safe centre centres when it fits and falls back to start
   when it does not. */
/* ---- this screen's own ground, and why the shared scrim is left alone.
   Settings is .hs-screen--sheet: it floats on .hm-overlay at 0.55 and lets what
   opened it show through, which is what the classic's own options dialog does.
   That scrim is SHARED and is not touched here. It was measured at 0.55 and it
   works where it has to: 1.9x separation over the title painting, 2.6x over the
   adventure map. It collapses to 1.0x over a town only because the town screen
   is 2.7x brighter than either, which is a fact about the town screen and not
   about this door - so moving the token would pay for this screen with that one.
   What this screen needed was more separation over the TITLE screen in
   particular, where "NEW GAME" sat behind the right-hand panel's edge and "QUIT"
   behind its foot, both legible enough to be read as part of the panel. A
   vignette centred on the frame puts the extra darkening exactly where those two
   words are and nowhere near the middle, where the panels cover the ground
   anyway.
   The grain is load-bearing too, not decoration: a wash is the one surface that
   measures as empty no matter how dark it is, and the margins down each side of
   this screen were 100% flat by a 32x32 scan before it. */
.hs-screen.hm-set {
  background-color: transparent;
  background-image:
    radial-gradient(112% 90% at 50% 46%,
      rgba(6,8,12,0) 0%, rgba(6,8,12,0.20) 62%, rgba(4,5,9,0.44) 100%),
    radial-gradient(120% 90% at 50% -10%, rgba(90,74,44,0.20) 0%, rgba(0,0,0,0) 62%),
    linear-gradient(178deg,
      rgba(19,15,10,0.34) 0%, rgba(19,15,10,0.10) 42%, rgba(19,15,10,0.52) 100%);
}
/* The grain rides ON the veil, not under it.
   Under it - which is where it started, on the screen's own background - the
   blur samples it and averages it away, and the ground came back flat: 47.2% of
   the frame measured blank with the blur on and the grain below it, against
   7.9% with the grain and no blur. Painted by the veil itself it is composited
   after the blur, so both effects survive: the words behind go, the material
   stays. */
.hm-set__veil {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-color: #0d0906;
  background-image:
    repeating-linear-gradient(118deg,
      rgba(255,226,182,0.05) 0 2px,
      rgba(0,0,0,0.10) 2px 7px),
    repeating-linear-gradient(28deg,
      rgba(255,232,190,0.035) 0 1px,
      rgba(0,0,0,0.07) 1px 11px);
}
.hm-set .hs-screen__body { align-items: stretch; }
.hm-set__stage { flex: 1 1 auto; align-self: stretch; min-width: 0; min-height: 0; }
/* The overlay is the scroller, so the cards fall down the plate rather than
   being centred in a box that then scrolls in both directions - the "safe
   centre" this replaces was there to stop exactly that. */
.hm-set .hm-plate__over {
  align-items: center;
  /* Safe centre, not centre: at 1366x768 the four cards are taller than the
     plate and a plain centre overflows in BOTH directions, which puts the top
     of the first card above the frame where nothing can scroll it back. Safe
     centres when it fits and falls back to the top when it does not - the same
     keyword, for the same reason, as the column this replaces. */
  justify-content: center;
  justify-content: safe center;
  padding: 14px 16px 18px;
  overflow: hidden;
}
.hm-set__cols {
  width: min(1480px, 96%);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: start;
  align-items: start;
  gap: 12px;
}
@media (max-width: 1100px) {
  .hm-set__cols { grid-template-columns: minmax(0, 1fr); width: min(760px, 96%); }
}
/* Everything that is a setting goes down the first column and the key
   reference takes the second on its own, floor to ceiling. Left to auto-place,
   the three setting cards zig-zagged across both columns and left a 580px hole
   under the shortest one. The list scrolls inside its own card rather than off
   the bottom edge of the frame, which is where it used to end - mid-row, on the
   letter H. */
.hm-set__cols > .hs-card { grid-column: 1; }
/* Two classes, to beat the child selector above: with one, the key card landed
   back in the first column and stacked on top of the settings it is a reference
   for. */
.hm-set__cols > .hs-card.hm-set__keys {
  grid-column: 2;
  /* span 3, not 1 / -1: with no explicit row track declared, -1 resolves to the
     first line of the explicit grid, so the card spanned ONE row - and that row
     then grew to the card's own height and left 365px of gap under the card
     beside it. The three settings cards are the three rows. */
  grid-row: 1 / span 3;
  align-self: stretch;
  min-height: 0;
}
/* The card stretches to the height of the three it stands beside, so the rows
   stretch with it rather than leaving a 250px hole under the last one: a
   reference table that ends two thirds of the way down a panel reads as a list
   that ran out. */
.hm-set__keylist {
  max-height: none;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.hm-set__keylist > .hs-listrow { flex: 1 1 auto; }
.hm-set__cols > .hs-card.hm-set__keys .hs-card__title { flex: 0 0 auto; }
.hm-set__cols > .hs-card.hm-set__keys { display: flex; flex-direction: column; }
@media (max-width: 1100px) {
  .hm-set__cols > .hs-card.hm-set__keys { grid-column: 1; grid-row: auto; }
}

.hm-set .hs-choices { flex-wrap: wrap; }
.hm-set .hs-choice { flex: 0 0 auto; min-width: 84px; }

/* ---- one serif on the screen.
   An art director counted three type families here: display serif headings,
   browser body serif at 11px for every note under a control, and monospace
   keycaps, and wrote "The classic never sets 11px grey body copy anywhere". The 11px
   body serif was '.hs-tiny' in 'styles.ts', which now sets the display family
   at 12px - that half is closed at the source and this screen does not restate
   it. What is left is the monospace: the key column is '.hs-num', and a key
   reference is the one place a fixed advance is doing work, so it stays and is
   set as a struck keycap instead of as code. */
.hm-set .hm-set__keylist .hs-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 7px 2px;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.08em;
  background-image: linear-gradient(180deg, #6b4a23 0%, #462f16 55%, #29190a 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 1px 0 rgba(255,232,186,0.28),
    0 1px 0 rgba(0,0,0,0.7);
}

/* The settings column is a scroller for the same reason New Game's is: at 768
   the three cards plus the key list are taller than the frame, and a flex item
   in an 'overflow: hidden' body has no overflow of its own to scroll. */
.hm-set__cols { max-height: 100%; scrollbar-gutter: stable; }
/* The same bottom fade New Game carries, and for the same measured reason: a
   native scrollbar is not painted into the reserved gutter in this build's
   capture environment, so the screen has to say for itself that it continues. */
.hm-set .hs-screen__body::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 34px;
  z-index: 3;
  pointer-events: none;
  background-image: linear-gradient(0deg,
    rgba(8,6,3,0.86) 0%, rgba(8,6,3,0.52) 42%, rgba(8,6,3,0.20) 72%, rgba(8,6,3,0) 100%);
}
`;

const SETTINGS_SHEET = `${FORM_ART_CSS}\n${SETTINGS_CSS}`;

function SettingsStyles(): null {
  useStyleSheet(SETTINGS_STYLE_ID, SETTINGS_SHEET, StyleLayer.screen);
  return null;
}
